/**
 * AuthAdapter - Unified Authentication Interface
 *
 * This interface abstracts all authentication providers, allowing the SPA
 * to interact with authentication in a provider-agnostic manner.
 *
 * Implemented by:
 * - EntraAdapter (Microsoft Entra ID / Azure AD)
 * - FirebaseGoogleAdapter (stub)
 * - FirebaseEmailAdapter (stub)
 */

/**
 * Represents an authenticated user with essential profile information
 */
export interface AuthUser {
  /** Unique identifier for the user (provider-specific) */
  id: string;

  /** User's email address */
  email: string;

  /** User's display name */
  displayName: string;

  /** URL to user's profile photo (optional) */
  photoUrl?: string;

  /** Raw ID token for downstream API calls */
  idToken?: string;

  /** Access token for API authorization */
  accessToken?: string;

  /** Token expiration timestamp (Unix milliseconds) */
  tokenExpiry?: number;
}

/**
 * Authentication provider types supported by the system
 *
 * Migration Note (2026-02-22):
 * 'entra' has been removed per Architecture Guardrail 1-006.
 * Use 'firebase-microsoft' for Microsoft/Entra ID authentication.
 */
export type AuthProviderType =
  | 'firebase-microsoft'
  | 'firebase-google'
  | 'firebase-email';

/**
 * Configuration for login behavior
 */
export interface LoginOptions {
  /** Use popup or redirect flow (default: popup) */
  interaction?: 'popup' | 'redirect';

  /** Additional scopes to request */
  scopes?: string[];

  /** Login hint (email) to pre-fill */
  loginHint?: string;

  /** Email address for email/password authentication */
  email?: string;

  /** Password for email/password authentication */
  password?: string;
}

/**
 * AuthAdapter Interface
 *
 * All authentication providers must implement this interface.
 * The SPA interacts only with this interface, never directly with
 * provider-specific libraries (MSAL, Firebase, etc.)
 */
export interface AuthAdapter {
  /** Identifier for this provider type */
  readonly providerType: AuthProviderType;

  /**
   * Initiates the login flow for this provider
   * @param options - Optional configuration for login behavior
   * @throws AuthError if login fails
   */
  login(options?: LoginOptions): Promise<void>;

  /**
   * Signs out the current user and clears session data
   * @throws AuthError if logout fails
   */
  logout(): Promise<void>;

  /**
   * Retrieves the currently authenticated user
   * @returns AuthUser if authenticated, null otherwise
   */
  getUser(): Promise<AuthUser | null>;

  /**
   * Retrieves roles assigned to the current user from the ID token
   * @returns Array of role strings (e.g., ['Admin', 'User'])
   */
  getRoles(): Promise<string[]>;

  /**
   * Checks if a user is currently authenticated
   * @returns true if authenticated, false otherwise
   */
  isAuthenticated(): boolean;

  /**
   * Silently acquires a new access token if the current one is expired
   * @returns Fresh access token or null if silent acquisition fails
   */
  acquireTokenSilent?(): Promise<string | null>;

  /**
   * Handles redirect callback after authentication redirect flow
   * This is needed for providers that use redirect-based auth
   */
  handleRedirectCallback?(): Promise<void>;
}

/**
 * Custom error class for authentication-related errors
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly provider: AuthProviderType,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Provider registry for managing available authentication adapters
 */
export interface AuthProviderRegistry {
  /** Register an adapter for a provider type */
  register(adapter: AuthAdapter): void;

  /** Get the adapter for a specific provider type */
  get(providerType: AuthProviderType): AuthAdapter | undefined;

  /** Get all registered adapters */
  getAll(): AuthAdapter[];

  /** Set the active/default provider */
  setActive(providerType: AuthProviderType): void;

  /** Get the currently active provider */
  getActive(): AuthAdapter | undefined;
}
